/**
 * FileManager Query Hooks
 *
 * React Query hooks for fetching file manager data.
 */

import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { FILE_MANAGER_STALE_TIMES, FILE_MANAGER_GC_TIMES } from "../configs/constants";
import { FILE_MANAGER_QUERY_KEYS } from "../configs/queryKeys";
import { FileManagerService } from "../services/file-manager.service";
import type {
  FolderQueryParams,
  EntriesQueryParams,
  NodesQueryParams,
  RootFolderPlaceholder,
} from "../services/file-manager.service.types";
import type { QueryClient } from "@tanstack/react-query";

// Re-export query keys for external usage
export { FILE_MANAGER_QUERY_KEYS };

/**
 * Helper function to invalidate related queries
 */
export const invalidateFileManagerQueries = (queryClient: QueryClient, folderId?: string | null) => {
  // Invalidate all file manager queries
  queryClient.invalidateQueries({ queryKey: FILE_MANAGER_QUERY_KEYS.all });

  // If we have a specific folder, also invalidate its details
  if (folderId !== null && folderId !== undefined) {
    queryClient.invalidateQueries({ queryKey: FILE_MANAGER_QUERY_KEYS.folderDetails(folderId) });
  }
};

// ============================================================================
// FOLDER QUERIES
// ============================================================================

/**
 * Query hook for fetching folders list
 */
export const useFoldersQuery = (params: FolderQueryParams = {}) => {
  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.foldersList(params),
    queryFn: () => FileManagerService.listFolders(params),
    staleTime: FILE_MANAGER_STALE_TIMES.FOLDERS,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

/**
 * Query hook for fetching root folders
 */
export const useRootFoldersQuery = (params: Omit<FolderQueryParams, "parent_id"> = {}) => {
  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.rootFolders(params),
    queryFn: () => FileManagerService.listRootFolders(params),
    staleTime: FILE_MANAGER_STALE_TIMES.FOLDERS,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

/**
 * Query hook for fetching folder details with contents
 */
export const useFolderDetailsQuery = (folderId: string | null) => {
  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.folderDetails(folderId),
    queryFn: () => {
      if (!folderId) throw new Error("Folder ID is required");
      return FileManagerService.getFolderDetails(folderId);
    },
    enabled: folderId !== null && folderId !== "root",
    staleTime: FILE_MANAGER_STALE_TIMES.FOLDER_DETAILS,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

/**
 * Query hook for navigating to folder by path
 */
export const useFolderNavigationQuery = (path: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.folderNavigation(path),
    queryFn: () => FileManagerService.navigateToFolderByPath(path),
    enabled: enabled && !!path,
    staleTime: FILE_MANAGER_STALE_TIMES.FOLDER_DETAILS,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

/**
 * Infinite query for folders with pagination
 */
export const useInfiniteFoldersQuery = (params: FolderQueryParams = {}) => {
  return useInfiniteQuery({
    queryKey: [...FILE_MANAGER_QUERY_KEYS.foldersList(params), "infinite"],
    queryFn: ({ pageParam = 1 }) =>
      FileManagerService.listFolders({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    staleTime: FILE_MANAGER_STALE_TIMES.FOLDERS,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

// ============================================================================
// ENTRY QUERIES
// ============================================================================

/**
 * Query hook for fetching entries in a folder
 */
export const useEntriesQuery = (folderId: string | null, params: EntriesQueryParams = {}) => {
  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.entriesList(folderId, params),
    queryFn: () => {
      if (!folderId) throw new Error("Folder ID is required");
      return FileManagerService.listEntries(folderId, params);
    },
    enabled: folderId !== null,
    staleTime: FILE_MANAGER_STALE_TIMES.ENTRIES,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

/**
 * Query hook for fetching entry details
 */
export const useEntryDetailsQuery = (folderId: string | null, entryId: string | null) => {
  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.entryDetails(folderId, entryId),
    queryFn: () => {
      if (!folderId || !entryId) throw new Error("Folder ID and Entry ID are required");
      return FileManagerService.getEntryDetails(folderId, entryId);
    },
    enabled: folderId !== null && entryId !== null,
    staleTime: FILE_MANAGER_STALE_TIMES.FOLDERS,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

/**
 * Infinite query for entries with pagination
 */
export const useInfiniteEntriesQuery = (folderId: string | null, params: EntriesQueryParams = {}) => {
  return useInfiniteQuery({
    queryKey: [...FILE_MANAGER_QUERY_KEYS.entriesList(folderId, params), "infinite"],
    queryFn: ({ pageParam = 1 }) => {
      if (!folderId) throw new Error("Folder ID is required");
      return FileManagerService.listEntries(folderId, { ...params, page: pageParam });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    enabled: folderId !== null,
    staleTime: FILE_MANAGER_STALE_TIMES.ENTRIES,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

// ============================================================================
// NODES QUERIES (COMBINED FOLDERS + ENTRIES)
// ============================================================================

/**
 * Query hook for fetching all nodes
 */
export const useAllNodesQuery = (params: NodesQueryParams = {}) => {
  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.nodesList(params),
    queryFn: () => FileManagerService.listAllNodes(params),
    staleTime: FILE_MANAGER_STALE_TIMES.FOLDERS,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

/**
 * Query hook for searching all nodes by name
 * Provides a simplified API for search functionality
 */
export const useListAllNodes = (params: {
  page?: number
  per_page?: number
  name_cont?: string
} = {}) => {
  // Transform params to the correct API format
  const apiParams: NodesQueryParams = {
    page: params.page,
    per_page: params.per_page,
    q: params.name_cont ? { name_cont: params.name_cont } : undefined,
  };

  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.nodesList(apiParams),
    queryFn: () => FileManagerService.listAllNodes(apiParams),
    staleTime: FILE_MANAGER_STALE_TIMES.SEARCH,
    gcTime: FILE_MANAGER_GC_TIMES.SEARCH,
  });
};

/**
 * Infinite query for all nodes with pagination
 */
export const useInfiniteAllNodesQuery = (params: NodesQueryParams = {}) => {
  return useInfiniteQuery({
    queryKey: [...FILE_MANAGER_QUERY_KEYS.nodesList(params), "infinite"],
    queryFn: ({ pageParam = 1 }) =>
      FileManagerService.listAllNodes({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    staleTime: FILE_MANAGER_STALE_TIMES.FOLDERS,
    gcTime: FILE_MANAGER_GC_TIMES.DEFAULT,
  });
};

/**
 * Counts the subfolders and files, at every depth, that deleting these folders would remove.
 */
export const useFolderContentsCountQuery = (folderIds: string[], enabled: boolean) => {
  const sortedIds = [...folderIds].sort();
  return useQuery({
    queryKey: FILE_MANAGER_QUERY_KEYS.folderContentsCount(sortedIds),
    queryFn: () => FileManagerService.countFolderContents(sortedIds),
    enabled: enabled && sortedIds.length > 0,
    // Always recount when the confirmation opens: the content may have changed since
    staleTime: 0,
    gcTime: 0,
  });
};

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Unified hook to get current folder data (root or specific folder)
 */
export const useCurrentFolderQuery = (folderId: string | null) => {
  const rootFoldersQuery = useRootFoldersQuery();
  const folderDetailsQuery = useFolderDetailsQuery(folderId);

  if (folderId === null || folderId === "root") {
    const rootData: RootFolderPlaceholder | undefined = rootFoldersQuery.data
      ? {
        id: null,
        name: "Racine",
        visibility: "public",
        parent_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        subfolders: rootFoldersQuery.data.folders,
        entries: [],
        subfolders_count: rootFoldersQuery.data.metadata?.total_items ?? rootFoldersQuery.data.folders.length,
        entries_count: 0,
      }
      : undefined;
    return {
      data: rootData,
      isLoading: rootFoldersQuery.isLoading,
      error: rootFoldersQuery.error,
      refetch: rootFoldersQuery.refetch,
    };
  }

  return {
    data: folderDetailsQuery.data?.folder,
    isLoading: folderDetailsQuery.isLoading,
    error: folderDetailsQuery.error,
    refetch: folderDetailsQuery.refetch,
  };
};
